'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { I18nextProvider } from 'react-i18next';

import {
  DEFAULT_PANEL_LANGUAGE,
  EVENT_PANEL_LANG_SYNC,
  LS_AGENT_PANEL_LANG,
  LS_USER_PANEL_LANG,
  PANEL_RTL_LANGS,
  normalizePanelLanguageLabel,
  panelLabelToLng,
} from '@/lib/panel-i18n/constants';
import i18n, { initPanelI18n } from '@/lib/panel-i18n/panelI18n';

// Sync init for hard URL SSR — do not wait for useEffect.
initPanelI18n();

function readStoredLang(zone) {
  if (typeof window === 'undefined') return DEFAULT_PANEL_LANGUAGE;
  const key = zone === 'agent' ? LS_AGENT_PANEL_LANG : LS_USER_PANEL_LANG;
  return normalizePanelLanguageLabel(window.localStorage.getItem(key));
}

function panelZone(pathname) {
  if (typeof pathname !== 'string') return null;
  if (pathname.startsWith('/agent')) return 'agent';
  if (pathname.startsWith('/user')) return 'user';
  return null;
}

/**
 * Always wraps children in I18nextProvider so auth providers below do not remount
 * when entering/leaving /user|/agent. Public pages simply do not call panel i18n.
 * Sets `dir`/`lang` on `<html>` only while inside those routes.
 */
export default function PanelI18nRoot({ children }) {
  const router = useRouter();
  const pathname = router.pathname || '';
  const zone = useMemo(() => panelZone(pathname), [pathname]);
  const [label, setLabel] = useState(DEFAULT_PANEL_LANGUAGE);

  useEffect(() => {
    initPanelI18n();
  }, []);

  useEffect(() => {
    if (!zone) return;
    setLabel(readStoredLang(zone));
  }, [zone]);

  useEffect(() => {
    const onSync = (e) => {
      const d = e?.detail;
      if (!d || typeof d.lang !== 'string') return;
      const z = d.zone === 'agent' ? 'agent' : 'user';
      if (z !== zone) return;
      setLabel(normalizePanelLanguageLabel(d.lang));
    };
    window.addEventListener(EVENT_PANEL_LANG_SYNC, onSync);
    return () => window.removeEventListener(EVENT_PANEL_LANG_SYNC, onSync);
  }, [zone]);

  const lng = useMemo(() => panelLabelToLng(label), [label]);

  useEffect(() => {
    if (!zone) return;
    void i18n.changeLanguage(lng);
  }, [lng, zone]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!zone) {
      document.documentElement.removeAttribute('dir');
      document.documentElement.lang = 'en';
      return;
    }
    const rtl = PANEL_RTL_LANGS.has(lng);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.lang = lng;
    return () => {
      document.documentElement.removeAttribute('dir');
      document.documentElement.lang = 'en';
    };
  }, [lng, zone]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
